import Phaser from 'phaser';
import type {
  GameRuntime,
  GameRuntimeConfig,
  GameRuntimeEvents,
  GameRuntimeState,
} from '@adgf/game-engine';

const GAME_WIDTH = 800;
const GAME_HEIGHT = 600;
const CELL = 140;
const GAP = 14;
const BOARD_PX = 3 * CELL + 2 * GAP;
const EMPTY = 0;
const COLOR_BG = 0x1b263b;
const COLOR_TILE = 0x415a77;
const COLOR_TILE_ALT = 0x778da9;

/**
 * PuzzleLogic — pure, Phaser-free 3x3 sliding-puzzle rules. 0 represents
 * the empty cell. Kept dependency-free for direct unit testing.
 */
export class PuzzleLogic {
  static readonly SIZE = 3;
  static readonly GOAL: readonly number[] = [1, 2, 3, 4, 5, 6, 7, 8, EMPTY];

  grid: number[];
  moves = 0;

  constructor(grid?: number[]) {
    this.grid = grid ? [...grid] : [...PuzzleLogic.GOAL];
  }

  emptyIndex(): number {
    return this.grid.indexOf(EMPTY);
  }

  /** Indices of tiles that are currently adjacent to the empty cell. */
  movableIndices(): number[] {
    return this.neighborsOf(this.emptyIndex());
  }

  /** Try to slide the tile at `index`. Returns true when it moved. */
  move(index: number): boolean {
    if (!this.movableIndices().includes(index)) return false;
    const empty = this.emptyIndex();
    this.grid[empty] = this.grid[index];
    this.grid[index] = EMPTY;
    this.moves++;
    return true;
  }

  isSolved(): boolean {
    return this.grid.every((value, i) => value === PuzzleLogic.GOAL[i]);
  }

  /**
   * Reset to the solved board, then walk `steps` random valid moves.
   * Because every move is reachable from the solved state, the resulting
   * board is guaranteed solvable.
   */
  shuffle(steps = 60, rng: () => number = Math.random): void {
    this.grid = [...PuzzleLogic.GOAL];
    this.moves = 0;
    let previousEmpty = -1;
    for (let s = 0; s < steps; s++) {
      const options = this.movableIndices().filter((i) => i !== previousEmpty);
      const chosen = options[Math.floor(rng() * options.length)];
      previousEmpty = this.emptyIndex();
      this.move(chosen);
    }
  }

  /** Score rewards fewer moves and a faster solve. */
  static computeScore(moves: number, seconds: number): number {
    return Math.max(100, 1000 - moves * 10 - Math.round(seconds) * 5);
  }

  private neighborsOf(index: number): number[] {
    const row = Math.floor(index / PuzzleLogic.SIZE);
    const col = index % PuzzleLogic.SIZE;
    const out: number[] = [];
    if (row > 0) out.push(index - PuzzleLogic.SIZE);
    if (row < PuzzleLogic.SIZE - 1) out.push(index + PuzzleLogic.SIZE);
    if (col > 0) out.push(index - 1);
    if (col < PuzzleLogic.SIZE - 1) out.push(index + 1);
    return out;
  }
}

interface SceneCallbacks {
  addPoints(points: number): void;
  onWin(score: number, moves: number, seconds: number): void;
  retry(): void;
  wantsStart(): boolean;
  onEvent(type: string, data?: Record<string, unknown>): void;
}

interface Tile {
  number: number;
  rect: Phaser.GameObjects.Rectangle;
  label: Phaser.GameObjects.Text;
}

/**
 * PuzzleScene — renders the 3x3 board, handles tile taps and the timer.
 */
class PuzzleScene extends Phaser.Scene {
  private readonly cb: SceneCallbacks;
  private readonly logic = new PuzzleLogic();

  private tiles: Map<number, Tile> = new Map();
  private movesText: Phaser.GameObjects.Text | null = null;
  private timeText: Phaser.GameObjects.Text | null = null;
  private readyOverlay: Phaser.GameObjects.Container | null = null;
  private winOverlay: Phaser.GameObjects.Container | null = null;

  private elapsedMs = 0;
  private running = false;
  private created = false;

  constructor(cb: SceneCallbacks) {
    super({ key: 'PuzzleScene' });
    this.cb = cb;
  }

  create(): void {
    const { width, height } = this.scale;

    this.movesText = this.add
      .text(90, 36, 'Moves: 0', { fontSize: '24px', color: '#e0e1dd' })
      .setOrigin(0.5);
    this.timeText = this.add
      .text(width - 90, 36, 'Time: 0s', { fontSize: '24px', color: '#778da9' })
      .setOrigin(0.5);

    this.buildTiles(width, height);
    this.buildReadyOverlay(width, height);
    this.buildWinOverlay(width, height);

    this.created = true;
    if (this.cb.wantsStart()) this.beginRound();
  }

  requestBegin(): void {
    if (this.created) this.beginRound();
  }

  /** Shuffle a fresh board and start the clock. */
  beginRound(): void {
    this.running = true;
    this.elapsedMs = 0;
    this.logic.shuffle(60);
    this.hideOverlay(this.readyOverlay);
    this.hideOverlay(this.winOverlay);
    this.movesText?.setText('Moves: 0');
    this.timeText?.setText('Time: 0s');
    this.refreshBoard();
  }

  resetRound(): void {
    this.running = false;
    this.elapsedMs = 0;
  }

  showFinal(score: number, moves: number, seconds: number): void {
    const label = this.winOverlay?.getByName('finalScoreText') as Phaser.GameObjects.Text | null;
    label?.setText(`Score: ${score}  ·  Moves: ${moves}  ·  Time: ${seconds}s`);
  }

  override update(_time: number, delta: number): void {
    if (!this.running) return;
    this.elapsedMs += delta;
    this.timeText?.setText(`Time: ${Math.floor(this.elapsedMs / 1000)}s`);
  }

  // ---- internals -------------------------------------------------------

  private cellCenter(index: number): { x: number; y: number } {
    const { width, height } = this.scale;
    const row = Math.floor(index / PuzzleLogic.SIZE);
    const col = index % PuzzleLogic.SIZE;
    const originX = width / 2 - BOARD_PX / 2 + CELL / 2;
    const originY = height / 2 - BOARD_PX / 2 + CELL / 2;
    return {
      x: originX + col * (CELL + GAP),
      y: originY + row * (CELL + GAP),
    };
  }

  private buildTiles(width: number, height: number): void {
    void width;
    void height;
    for (let n = 1; n <= 8; n++) {
      const { x, y } = this.cellCenter(n - 1);
      const rect = this.add.rectangle(x, y, CELL - 8, CELL - 8, COLOR_TILE);
      rect.setStrokeStyle(3, COLOR_TILE_ALT);
      const label = this.add
        .text(x, y, String(n), { fontSize: '52px', color: '#e0e1dd', fontStyle: 'bold' })
        .setOrigin(0.5);
      rect.setInteractive({ useHandCursor: true });
      rect.on('pointerdown', () => this.onTilePressed(n));
      this.tiles.set(n, { number: n, rect, label });
    }
  }

  private onTilePressed(number: number): void {
    if (!this.running) return;
    const cellIndex = this.logic.grid.indexOf(number);
    const moved = this.logic.move(cellIndex);
    if (!moved) {
      this.cb.onEvent('invalid_move', { number });
      return;
    }
    this.movesText?.setText(`Moves: ${this.logic.moves}`);
    this.cb.onEvent('move', { number });
    this.refreshBoard();

    if (this.logic.isSolved()) {
      this.finishWin();
    }
  }

  /** Reposition every tile to match the current grid. */
  private refreshBoard(): void {
    for (const [, tile] of this.tiles) {
      const cellIndex = this.logic.grid.indexOf(tile.number);
      const { x, y } = this.cellCenter(cellIndex);
      tile.rect.setPosition(x, y);
      tile.label.setPosition(x, y);
    }
  }

  private finishWin(): void {
    this.running = false;
    const seconds = Math.floor(this.elapsedMs / 1000);
    const score = PuzzleLogic.computeScore(this.logic.moves, seconds);
    this.cb.addPoints(score);
    this.cb.onEvent('won', { moves: this.logic.moves, seconds });
    this.showOverlay(this.winOverlay);
    this.cb.onWin(score, this.logic.moves, seconds);
  }

  private buildReadyOverlay(width: number, height: number): void {
    this.readyOverlay = this.add.container(width / 2, height / 2);
    const title = this.add.text(0, -40, 'Mini Puzzle', {
      fontSize: '36px',
      color: '#ffffff',
      fontStyle: 'bold',
    });
    title.setOrigin(0.5);
    const hint = this.add.text(0, 20, 'Slide the tiles into order 1 -> 8', {
      fontSize: '18px',
      color: '#a0a0a0',
    });
    hint.setOrigin(0.5);
    const btn = this.add.rectangle(0, 90, 220, 64, COLOR_TILE_ALT);
    const btnText = this.add.text(0, 90, 'START', {
      fontSize: '24px',
      color: '#1b263b',
      fontStyle: 'bold',
    });
    btnText.setOrigin(0.5);
    btn.setInteractive({ useHandCursor: true });
    btn.on('pointerdown', () => this.cb.wantsStart() || this.requestBegin());
    this.readyOverlay.add([title, hint, btn, btnText]);
  }

  private buildWinOverlay(width: number, height: number): void {
    this.winOverlay = this.add.container(width / 2, height / 2);
    const title = this.add.text(0, -60, 'Solved!', {
      fontSize: '40px',
      color: '#2ecc71',
      fontStyle: 'bold',
    });
    title.setOrigin(0.5);
    const summary = this.add.text(0, -10, 'Score: 0', {
      fontSize: '24px',
      color: '#e0e1dd',
    });
    summary.setOrigin(0.5);
    summary.setName('finalScoreText');
    const btn = this.add.rectangle(0, 70, 260, 64, COLOR_TILE_ALT);
    const btnText = this.add.text(0, 70, 'Play Again', {
      fontSize: '24px',
      color: '#1b263b',
      fontStyle: 'bold',
    });
    btnText.setOrigin(0.5);
    btn.setInteractive({ useHandCursor: true });
    btn.on('pointerdown', () => this.cb.retry());
    this.winOverlay.add([title, summary, btn, btnText]);
    this.winOverlay.setVisible(false);
  }

  private showOverlay(overlay: Phaser.GameObjects.Container | null): void {
    overlay?.setVisible(true);
    overlay?.setActive(true);
  }

  private hideOverlay(overlay: Phaser.GameObjects.Container | null): void {
    overlay?.setVisible(false);
    overlay?.setActive(false);
  }
}

/**
 * PuzzleGame — GameRuntime wrapper. Owns the Phaser.Game instance, the
 * score and the runtime state machine.
 */
export class PuzzleGame implements GameRuntime {
  private readonly config: GameRuntimeConfig;
  private readonly events: GameRuntimeEvents;
  private state: GameRuntimeState = 'idle';
  private score = 0;
  private wantStart = false;
  private game: Phaser.Game | null = null;
  private scene: PuzzleScene | null = null;

  constructor(config: GameRuntimeConfig, events: GameRuntimeEvents = {}) {
    this.config = config;
    this.events = events;
  }

  mount(): void {
    if (this.state !== 'idle') return;

    this.scene = new PuzzleScene(this.buildCallbacks());

    this.game = new Phaser.Game({
      type: Phaser.AUTO,
      parent: this.config.parent,
      width: this.config.width ?? GAME_WIDTH,
      height: this.config.height ?? GAME_HEIGHT,
      backgroundColor: COLOR_BG,
      scale: {
        mode: Phaser.Scale.FIT,
        width: this.config.width ?? GAME_WIDTH,
        height: this.config.height ?? GAME_HEIGHT,
      },
      scene: [this.scene],
    });

    this.state = 'mounted';
  }

  start(): void {
    this.wantStart = true;
    if (
      this.state === 'mounted' ||
      this.state === 'paused' ||
      this.state === 'gameover'
    ) {
      this.state = 'running';
      this.events.onStart?.();
      this.scene?.requestBegin();
    }
  }

  pause(): void {
    if (this.state !== 'running') return;
    this.state = 'paused';
    this.scene?.scene?.pause();
    this.events.onPause?.();
  }

  resume(): void {
    if (this.state !== 'paused') return;
    this.state = 'running';
    this.scene?.scene?.resume();
    this.events.onResume?.();
  }

  destroy(): void {
    if (this.state === 'destroyed') return;
    this.game?.destroy(true);
    this.game = null;
    this.scene = null;
    this.state = 'destroyed';
  }

  getScore(): number {
    return this.score;
  }

  getState(): GameRuntimeState {
    return this.state;
  }

  retry(): void {
    this.score = 0;
    this.events.onScoreChange?.(this.score);
    this.scene?.resetRound();
    this.state = 'running';
    this.events.onRetry?.();
    this.wantStart = true;
    this.scene?.requestBegin();
  }

  private buildCallbacks(): SceneCallbacks {
    return {
      addPoints: (points: number) => {
        this.score += points;
        this.events.onScoreChange?.(this.score);
      },
      onWin: (finalScore: number, moves: number, seconds: number) => {
        this.state = 'gameover';
        this.scene?.showFinal(finalScore, moves, seconds);
        this.events.onGameOver?.(finalScore, { moves, seconds });
      },
      retry: () => this.retry(),
      wantsStart: () => this.wantStart,
      onEvent: (type: string, data?: Record<string, unknown>) => {
        this.events.onEvent?.(type, data);
      },
    };
  }
}
